import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import useBackgroundAudio from '../hooks/useBackgroundAudio';

/**
 * STABILIZE + ANCHOR
 * 
 * A single scene with two sequential phases that appears automatically after Translate.
 * No branching, no buttons, no ability to skip.
 * 
 * Phase 1 - Light Grounding (Stabilize):
 *   - Pulsing dot near lower center
 *   - Microcopy: "Let the body arrive where it is."
 *   - Tap anywhere to proceed OR auto-resolves after ~35 seconds
 * 
 * Phase 2 - Subtle Realism Anchor:
 *   - Dot fades
 *   - Microcopy: "Holding this may change how others respond."
 *   - Auto-proceeds to Close
 */

const StabilizeAnchor = ({ onComplete }) => {
  const [phase, setPhase] = useState(1); // 1 = stabilize, 2 = anchor
  const [showStabilizeMicrocopy, setShowStabilizeMicrocopy] = useState(false);
  const [showAnchorMicrocopy, setShowAnchorMicrocopy] = useState(false);
  const [showDot, setShowDot] = useState(true);

  // Subtle background audio
  useBackgroundAudio(true);

  // Phase 1: Stabilize (Light Grounding)
  useEffect(() => {
    if (phase === 1) {
      // Show microcopy after brief delay
      const showMicrocopyTimer = setTimeout(() => {
        setShowStabilizeMicrocopy(true);
      }, 1500);

      // Hide microcopy after it's been visible for 4 seconds (extended +2s)
      const hideMicrocopyTimer = setTimeout(() => {
        setShowStabilizeMicrocopy(false);
      }, 6000);

      // Auto-advance to Phase 2 after ~35 seconds if user doesn't tap
      const autoAdvanceTimer = setTimeout(() => {
        advanceToAnchor();
      }, 35000);

      return () => {
        clearTimeout(showMicrocopyTimer);
        clearTimeout(hideMicrocopyTimer);
        clearTimeout(autoAdvanceTimer);
      };
    }
  }, [phase]);

  // Phase 2: Anchor (Subtle Realism)
  useEffect(() => {
    if (phase === 2) {
      // Fade out dot
      const fadeDotTimer = setTimeout(() => {
        setShowDot(false);
      }, 1000);

      // Show anchor microcopy
      const showAnchorTimer = setTimeout(() => {
        setShowAnchorMicrocopy(true);
      }, 2000);

      // Hide anchor microcopy and proceed to Close (extended +2s)
      const hideAnchorTimer = setTimeout(() => {
        setShowAnchorMicrocopy(false);
      }, 7500);

      // Complete and proceed to Close (extended +2s)
      const completeTimer = setTimeout(() => {
        onComplete();
      }, 9000);

      return () => {
        clearTimeout(fadeDotTimer);
        clearTimeout(showAnchorTimer);
        clearTimeout(hideAnchorTimer);
        clearTimeout(completeTimer);
      };
    }
  }, [phase, onComplete]);

  // Advance from Stabilize to Anchor
  const advanceToAnchor = useCallback(() => {
    if (phase === 1) {
      setShowStabilizeMicrocopy(false);
      setPhase(2);
    }
  }, [phase]);

  // Handle tap - only advances during Phase 1
  const handleTap = useCallback(() => {
    if (phase === 1) {
      // Haptic feedback
      if ('vibrate' in navigator) {
        navigator.vibrate(5);
      }
      advanceToAnchor();
    }
    // Phase 2 ignores taps - user is carried forward automatically
  }, [phase, advanceToAnchor]);

  return (
    <motion.div
      className="fixed inset-0 z-50 overflow-hidden cursor-pointer"
      style={{ backgroundColor: 'hsl(240, 8%, 3%)' }}
      onClick={handleTap}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 2 }}
    >
      {/* Weighted Dot - visible in Phase 1, fades in Phase 2 */}
      <AnimatePresence>
        {showDot && (
          <motion.div
            className="absolute"
            style={{
              left: '50%',
              bottom: '30%',
              transform: 'translateX(-50%)'
            }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 2 }}
          >
            {/* Pulsing dot - slow, steady, no breath pacing */}
            <motion.div
              className="rounded-full"
              style={{
                width: 14,
                height: 14,
                backgroundColor: 'hsl(240, 6%, 12%)',
                boxShadow: '0 0 25px hsl(240, 8%, 8%)'
              }}
              animate={{
                scale: [1, 1.12, 1],
                opacity: [0.7, 0.85, 0.7]
              }}
              transition={{
                duration: 4.5,
                repeat: Infinity,
                ease: 'easeInOut'
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Phase 1 Microcopy: "Let the body arrive where it is." */}
      <AnimatePresence>
        {showStabilizeMicrocopy && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.7 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5 }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 font-display text-sm sm:text-base text-center px-6"
            style={{ color: 'hsl(40, 12%, 68%)' }}
          >
            Let the body arrive where it is.
          </motion.p>
        )}
      </AnimatePresence>

      {/* Phase 2 Microcopy: "Holding this may change how others respond." */}
      <AnimatePresence>
        {showAnchorMicrocopy && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.7 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5 }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 font-display text-sm sm:text-base text-center max-w-md px-6"
            style={{ color: 'hsl(40, 12%, 68%)' }}
          >
            Holding this may change how others respond.
          </motion.p>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default StabilizeAnchor;
